/**
 * 千葉県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は福岡・北海道・山形と同じテンプレート。
 * 進行の軸は「物理的な大きさ ＝ ごちそう度」で、県ごとに触らない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 房州びわを「日本一」と書かない。県公式は栽培面積・収穫量・産出額とも全国2位。
 *  - なめろうの「皿をなめるほど旨い」は由来説。断定形にしない。
 *  - くじらのたれの名の由来は二説あり、どちらとも断定しない。
 *  - 「竹岡式」という語を定義した公的資料は見つからなかったので、表示名は「竹岡のラーメン」。
 *    発祥年・発祥店も書かない。
 *  - 太巻き祭りずしの「紀州のめはりずしがルーツ」は伝聞。断定しない。
 *  - 伊勢えびの全国順位は年次で変わるので、年を必ず添える。
 *  - 捕鯨は事実の記述にとどめ、価値判断を加えない。
 *
 * 本番画像は public/assets/foods/chiba/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：房州びわ・くじらのたれ・太巻き祭りずし・あさりのふうかし（店で食べられる品に差し替え）
 * - ホワイト餃子：皮の厚さ・多めの油で焼く焼き方・創業や由来は、公式資料（野田市観光協会のページ）に書かれていないので書かない
 * - ホワイト餃子：店の住所・営業時間・値段は書かない（すぐ変わる情報で、店の宣伝にもなる）
 * - 九十九里の焼きはまぐり：「焼きはまぐりの元祖」は店の自称で公式の裏付けが無いため書かない
 * - 九十九里の焼きはまぐり：特定の店名は書かない
 * - 鴨川のおらが丼：始まった年・考えた団体は今回開いた資料に無いため書かない
 * - 鴨川のおらが丼：店の数（約40店）は変わりうるが、出典の記述として tip に入れた。外すかは人が判断
 * - 勝浦・房総の金目鯛の煮付け：特定の店名は書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { CHIBA_FOOD_HULLS } from './chibaFoodShapes.generated';
import { chibaSceneryTour } from './chibaScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'yude-rakkasei',
    level: 1,
    name: 'ゆで落花生',
    shortName: '落花生',
    image: '/assets/foods/chiba/01_yude_rakkasei.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '採れたてでしか作れない、産地だけの食べ方。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/yude_rakkasei_chiba.html',
    icon: { label: '落花生', shape: 'roe', baseColor: '#d8b478', accentColor: '#8a5c2c' },
    reward: {
      unlockScore: 200,
      area: '八街・千葉',
      headline: '落花生の生産量は全国1位。国内のおよそ7割が千葉県産。',
      tip: 'ゆで落花生は生の採れたてからしか作れないので、産地でしか出会いにくい。火山灰由来の関東ローム層が栽培に向いている。',
      links: searchLinks('八街市', 'ゆで落花生'),
    },
  },
  {
    id: 'namero',
    level: 2,
    name: 'なめろう',
    shortName: 'なめろう',
    image: '/assets/foods/chiba/02_namero.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '船の上でも食べられるよう、たたいて粘らせた。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/namerou_chiba.html',
    icon: { label: 'なめろう', shape: 'roe', baseColor: '#9c8464', accentColor: '#4d6b3a' },
    reward: {
      unlockScore: 500,
      area: '房総沿岸',
      headline: '波で醤油がこぼれる船の上でも食べられるよう、粘りの強い味噌で魚をたたいた漁師料理。',
      tip: '基本はアジに長ねぎ・しょうが・しそ・味噌。季節によってイワシやサンマ、トビウオも使う。',
      links: searchLinks('南房総', 'なめろう'),
    },
  },
  {
    id: 'sanga-yaki',
    level: 3,
    name: 'さんが焼き',
    shortName: 'さんが焼き',
    image: '/assets/foods/chiba/03_sanga_yaki.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'なめろうを貝殻に詰めて、焼く。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/sanga_yaki_chiba.html',
    icon: { label: 'さんが', shape: 'mochi', baseColor: '#a8875e', accentColor: '#e4dcc8' },
    reward: {
      unlockScore: 1000,
      area: '房総半島',
      headline: 'なめろうをアワビの貝殻に詰めて山へ持って行き、山小屋で焼いて食べたのが始まりとされる。',
      tip: '千葉の古い言い方で「○○の家」を「○○が」と訛ることから、山の家で食べた料理＝「さんが焼き」。',
      links: searchLinks('房総', 'さんが焼き'),
    },
  },
  {
    id: 'iwashi-dangojiru',
    level: 4,
    name: 'いわしのだんご汁',
    shortName: 'だんご汁',
    image: '/assets/foods/chiba/04_iwashi_dangojiru.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '九十九里のいわしを、すり身にして汁へ。',
    source: 'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/area/chiba.html',
    icon: { label: 'だんご汁', shape: 'pot', baseColor: '#8a9a7c', accentColor: '#f0e8d4' },
    reward: {
      unlockScore: 1800,
      area: '九十九里',
      headline: '九十九里に揚がるいわしをすり身にして団子にし、汁に仕立てた郷土料理。',
      tip: '農林水産省「うちの郷土料理」の千葉県の一覧に載っている一品。九十九里は旭市の刑部岬からいすみ市の太東岬まで、66キロの砂浜が続く。',
      links: searchLinks('九十九里', 'いわしのだんご汁'),
    },
  },
  {
    id: 'katsuura-tantanmen',
    level: 5,
    name: '勝浦タンタンメン',
    shortName: '勝浦タンタン',
    image: '/assets/foods/chiba/05_katsuura_tantanmen.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: 'ごまではなく、醤油とラー油。',
    source: 'https://maruchiba.jp/gourmet/feature/detail_148.html',
    icon: { label: '担担麺', shape: 'ramen', baseColor: '#c1442f', accentColor: '#f0dcb8' },
    reward: {
      unlockScore: 3000,
      area: '勝浦',
      headline: 'ごま系の担担麺とは違い、醤油ベースのスープにラー油をたっぷり使う。具は玉ねぎのみじん切りと挽肉。',
      tip: '漁業のさかんな勝浦で、寒い海仕事のあとに体を温めるメニューとして広まった。1954年に提供が始まり、いまは県内外40店舗が認定を受けている。',
      links: searchLinks('勝浦市', '勝浦タンタンメン'),
    },
  },
  {
    id: 'takeoka-ramen',
    level: 6,
    name: '竹岡のラーメン',
    shortName: '竹岡ラーメン',
    image: '/assets/foods/chiba/06_takeoka_ramen.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: 'チャーシューの煮汁をお湯で割った、黒いスープ。',
    source: 'https://maruchiba.jp/gourmet/spot/detail_11108.html',
    icon: { label: '竹岡', shape: 'ramen', baseColor: '#3a2c22', accentColor: '#e8d8b8' },
    reward: {
      unlockScore: 4500,
      area: '富津市竹岡',
      headline: '千葉県公式観光サイトが「内房を代表するラーメンの元祖」と紹介する、富津市竹岡の味。',
      tip: '黒いスープはチャーシューの煮汁をお湯で割った醤油味。玉ねぎを細かく刻んだ「ヤクミ」を添えるのが定番。',
      links: searchLinks('富津市', '竹岡 ラーメン'),
    },
  },
  {
    id: 'noda-white-gyoza',
    level: 7,
    name: 'ホワイト餃子',
    shortName: 'ホワイト餃子',
    image: '/assets/foods/chiba/07_noda_white_gyoza.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '野田市で親しまれている焼き餃子。さっくりとした皮が特徴で、市の観光協会は野田市民のソウルフードと紹介している。この味を求めて、市内外から多くの餃子好きが集まる。',
    source:
      'https://www.kanko-nodacity.jp/3488/',
    icon: { label: '餃子', shape: 'dumpling', baseColor: '#e8c27a', accentColor: '#9a6a2e' },
    reward: {
      unlockScore: 6500,
      area: '野田市',
      headline: '野田市民のソウルフード',
      tip: '野田市観光協会は、野田市民の家庭の冷凍庫にはホワイト餃子が常備されているはず、と紹介している。',
      links: searchLinks('野田市', 'ホワイト餃子'),
      official: [{ label: '野田市観光協会「ホワイト餃子」', url: 'https://www.kanko-nodacity.jp/3488/' }],
    },
  },
  {
    id: 'kujukuri-yaki-hamaguri',
    level: 8,
    name: '九十九里の焼きはまぐり',
    shortName: '焼きはまぐり',
    image: '/assets/foods/chiba/08_kujukuri_yaki_hamaguri.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '大粒のはまぐりを殻のまま網で焼く。口が少し開いたらたまった汁を捨て、しっかり開いてからしょうゆだれをかけ、汁がぐつぐつし始めたら食べごろ。焼くと身は上の殻にくっつく。',
    source:
      'https://maruchiba.jp/gourmet/feature/detail_302.html',
    icon: { label: 'はまぐり', shape: 'sashimi-platter', baseColor: '#8c8378', accentColor: '#e8b25a' },
    reward: {
      unlockScore: 9000,
      area: '九十九里浜沿岸（九十九里町ほか）',
      headline: '殻ごと網で焼くはまぐり',
      tip: '九十九里浜ははまぐりの名産地で、海沿いは「ハマグリ街道」と呼ばれる。漁のシーズンは11月から翌8月と長く、産卵前の春が特においしいとされる。',
      links: searchLinks('九十九里町', '焼きはまぐり'),
      official: [{ label: 'ちば観光ナビ「千葉の蛤（はまぐり）を食べられるお店9選！」', url: 'https://maruchiba.jp/gourmet/feature/detail_302.html' }, { label: 'ちば観光ナビ「千葉の貝料理特集」', url: 'https://maruchiba.jp/gourmet/feature/detail_40.html' }],
    },
  },
  {
    id: 'kamogawa-oraga-don',
    level: 9,
    name: '鴨川のおらが丼',
    shortName: 'おらが丼',
    image: '/assets/foods/chiba/09_kamogawa_oraga_don.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '「おらが」は房州の言葉で「我が家」「家では」という意味。鴨川の店がそれぞれ趣向をこらして作るご当地丼で、地元の漁港であがった地魚をたっぷり使った海鮮丼などがある。',
    source:
      'https://maruchiba.jp/gourmet/feature/detail_59.html',
    icon: { label: 'おらが丼', shape: 'rice-bowl', baseColor: '#d24a3a', accentColor: '#f3ecd9' },
    reward: {
      unlockScore: 12000,
      area: '鴨川市',
      headline: '店ごとに中身がちがう我が家の丼',
      tip: '「新鮮な地元の食材を使うこと」「季節感を失わない」「健康を意識した商品づくり」が「おらが丼のおきて」とされ、約40店が丼を出している。',
      links: searchLinks('鴨川市', 'おらが丼'),
      official: [{ label: 'ちば観光ナビ「千葉県のおすすめ海鮮丼・ご当地丼特集」', url: 'https://maruchiba.jp/gourmet/feature/detail_59.html' }],
    },
  },
  {
    id: 'hakarime-don',
    level: 10,
    name: 'はかりめ丼',
    shortName: 'はかりめ丼',
    image: '/assets/foods/chiba/10_hakarime_don.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '富津の穴子を、たっぷり飯の上へ。',
    source: 'https://www.futtsu-kanko.info/hakarime/',
    icon: { label: 'はかりめ', shape: 'rice-bowl', baseColor: '#8a5a32', accentColor: '#f3e6c6' },
    reward: {
      unlockScore: 16000,
      area: '富津',
      headline: '地元で穴子を「はかりめ」と呼ぶのは、細長い体の点々とした模様が棒はかりの目盛りに見えるから。',
      tip: '煮穴子、焼き穴子と、店ごとに仕立てが違う。富津市の名産。',
      links: searchLinks('富津市', 'はかりめ丼'),
    },
  },
  {
    id: 'boso-kinmedai-nitsuke',
    level: 11,
    name: '勝浦・房総の金目鯛の煮付け',
    shortName: '金目鯛の煮付け',
    image: '/assets/foods/chiba/11_boso_kinmedai_nitsuke.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '朱色の姿から縁起のよい魚として親しまれる金目鯛を、甘辛く煮付ける。千葉では銚子沖、勝浦沖、東京湾口（布良瀬漁場）の3か所で脂ののった金目鯛が水揚げされる。',
    source:
      'https://maruchiba.jp/gourmet/feature/detail_79.html',
    icon: { label: '金目鯛', shape: 'fish', baseColor: '#d0362b', accentColor: '#7a3d14' },
    reward: {
      unlockScore: 22000,
      area: '勝浦市ほか外房',
      headline: '朱色の縁起魚を甘辛く煮る',
      tip: '「つりきんめ」は立て縄と呼ばれる一本釣りで1尾ずつ釣り上げるので、魚体に傷がつきにくい。勝浦沖の「外房つりきんめ鯛」は千葉ブランド水産物に認定されている。',
      links: searchLinks('勝浦市', '金目鯛の煮付け'),
      official: [{ label: 'ちば観光ナビ「千葉の海鮮料理特集」', url: 'https://maruchiba.jp/gourmet/feature/detail_79.html' }],
    },
  },
  {
    id: 'sotobo-iseebi',
    level: 12,
    name: '外房イセエビ',
    shortName: '伊勢海老',
    image: '/assets/foods/chiba/12_sotobo_iseebi.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '夜明け前に網を上げる、外房の主役。',
    source: 'https://www.pref.chiba.lg.jp/suisan/brand/ninteihin/sotobouiseebi.html',
    icon: { label: '伊勢海老', shape: 'feast', baseColor: '#c1392b', accentColor: '#f4d9a8' },
    reward: {
      unlockScore: 30000,
      area: '大原・勝浦・御宿',
      headline: '「刺網」で海底を移動するエビを絡めて獲る。品質を落とさないよう、夜明け前から網を上げる。',
      tip: '千葉ブランド水産物の規格は体長13センチ超。令和4年の漁業・養殖業生産統計年報では、いせえびの生産量は全国1位。',
      links: searchLinks('いすみ市', '伊勢海老'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const chibaFoodStage: StageDefinition = {
  regionId: 'chiba',
  regionName: '千葉県',
  mode: 'food',
  title: '千葉めしマージ',
  theme: {
    primaryColor: '#2f6fa8',
    secondaryColor: '#e8b93a',
    accentColor: '#f5d873',
    backgroundColor: '#161a20',
  },
  /** 背景ツアー「千葉めぐり」。軸は季節。5月から翌4月へ一周する。 */
  scenery: {
    tour: chibaSceneryTour,
  },
  // 画像の輪郭（自動生成）を付与する。
  items: attachHulls(FOOD_ITEMS, CHIBA_FOOD_HULLS),
};
